import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';

import Global from '../../../../store/global-selectors';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import SidebarItemList from './item-list';

import messages from './messages';
import style from './grouped-order-items.module.less';

const fulfillmentPlanIdToItemSelector = createSelector(
  (state, fulfillmentPlanIds) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }),
  (state) => Global.selectEntitiesSalesOrders(state),
  (fulfillmentPlans = emptyArray, salesOrders = emptyObject) => {
    const itemGroupingsBySalesOrder = fulfillmentPlans.reduce((groupings, fp) => {
      if (!fp) {
        return groupings;
      }
      const { salesOrdersFulfilledIds, items: fpItems } = fp;
      const matchedSalesOrders = salesOrdersFulfilledIds.map((salesOrderId) => salesOrders[salesOrderId]);

      const mappedSalesOrders = matchedSalesOrders.map((matchedOrder) => {
        return {
          orderNumber: matchedOrder.orderNumber,
          salesOrderId: matchedOrder.salesOrderId,
          mappedItems: fpItems.filter((fpItem) => {
            const salesOrderItems = matchedOrder && matchedOrder.items ? matchedOrder.items : [];
            // prefer salesOrderItemId but it may be null, so set up fallbacks in case that is true
            if (fpItem.salesOrderItemId) {
              return salesOrderItems.find((item) => item.salesOrderItemId === fpItem.salesOrderItemId);
            }

            if (fpItem.sku) {
              return salesOrderItems.find((item) => item.sku === fpItem.sku);
            }
            return salesOrderItems.find((item) => item.name === fpItem.name);
          }),
        };
      });

      const grouping = {
        fulfillmentPlanId: fp.fulfillmentPlanId,
        itemGroupings: mappedSalesOrders,
      };
      return [...groupings, grouping];
    }, []);
    return itemGroupingsBySalesOrder;
  },
);

const ItemTitle = ({ itemsCount, groupingsCount, groupCount, orderNumber }) => {
  if (itemsCount > 1 || groupingsCount > 1) {
    return (
      <div className={style['item-list-title-label']}>
        <FormattedMessage {...messages.orderNumber} values={{ orderNumber }} />
      </div>
    );
  }

  return <FormattedMessage {...messages.defaultTitle} values={{ count: groupCount }} />;
};

ItemTitle.propTypes = {
  itemsCount: PropTypes.number.isRequired,
  groupingsCount: PropTypes.number.isRequired,
  groupCount: PropTypes.number.isRequired,
  orderNumber: PropTypes.string.isRequired,
};

/**
 * @param {object} props
 * @param {boolean} [props.willInventoryShow]
 * @param {(productId: string) => void} props.openProductDetailModal
 * @param {string[]} props.fulfillmentPlanIds
 * @param {string} props.itemsListContainerClassName
 * @returns {JSX.Element|null}
 */
export const GroupByOrderNumber = ({
  willInventoryShow = false,
  openProductDetailModal,
  fulfillmentPlanIds,
  itemsListContainerClassName,
}) => {
  const items = useSelector((state) => fulfillmentPlanIdToItemSelector(state, fulfillmentPlanIds));
  const itemsCount = items.length;

  if (items.length === 0) {
    return null;
  }
  const itemListClass = classnames(style['item-list'], {
    [style['inventory-grid']]: willInventoryShow,
    [style['non-inventory-grid']]: !willInventoryShow,
  });
  return (
    <div className={classnames(style['item-list-wrapper'], itemsListContainerClassName)}>
      <div className={itemListClass}>
        {items.map((item) => {
          const groupingsCount = item.itemGroupings.length;
          return item.itemGroupings.map((itemGroup) => {
            const groupCount = itemGroup.mappedItems.length;
            if (!groupCount) {
              return null;
            }
            const { fulfillmentPlanId } = item;
            const { orderNumber, salesOrderId } = itemGroup;
            return (
              <SidebarItemList
                key={salesOrderId}
                fulfillmentPlanId={fulfillmentPlanId}
                items={itemGroup.mappedItems}
                openProductDetailModal={openProductDetailModal}
                willInventoryShow={willInventoryShow}
              >
                <div className={classnames(style['item-list-header'], style['item-list-title'])}>
                  <ItemTitle groupCount={groupCount} groupingsCount={groupingsCount} itemsCount={itemsCount} orderNumber={orderNumber} />
                </div>
              </SidebarItemList>
            );
          });
        })}
      </div>
    </div>
  );
};
